// client/src/pages/admin/adminStudentManagement.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import CustomAlert from '../../components/customs/customAlert';
import BackButton from '../../components/customs/backButton';
import showNotification from '../../utilities/showNotification';
import { GlobalContext } from '../../context/provider';
import useStudent from '../../hooks/useStudent';
import useCourse from '../../hooks/useCourse';
import useMessage from '../../hooks/useMessage';

import '../../styles/globalstyles.css';
import './adminStudentManagement.css';
import '../students/studentCourseManagement.css'

const AdminStudentManagement = () => {
  const navigate = useNavigate();
  const { authState: { authData } } = useContext(GlobalContext);
  
  const {
    getAllStudents,
    updateStudent,
    deleteStudent,
    getCourseProgress,
    getCourseGrade,
    getLabProgress,      
    getQuizProgress,     
    getExamProgress,     
    getCapstoneProgress,
    getPaymentHistory,   
    getSubscriptions,    
    cancelSubscription,  
    refundPayment,       
    refundToCredit,
    enrollCourse,
    unenrollCourse,
  } = useStudent();

  const { getAllCourses, getCourseById } = useCourse();
  
  const { 
    getMessagesAndMarkSeen,
    sendMessage,
  } = useMessage();

  const [isLoading, setIsLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [detailedProgress, setDetailedProgress] = useState({});       // { [courseId]: {...} }
  const [selectedProgressCourseId, setSelectedProgressCourseId] = useState(null);
  
  const [modalVisible, setModalVisible] = useState(false);
  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [billingModalVisible, setBillingModalVisible] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);

  const [billingData, setBillingData] = useState({
    payments: [],
    subscriptions: [],
  });
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingTab, setBillingTab] = useState('payments'); // 'payments' | 'subscriptions'
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [refundType, setRefundType] = useState(''); // 'refund' | 'credit'

  const [coursesModalVisible, setCoursesModalVisible] = useState(false);
  const [enrollSearchQuery, setEnrollSearchQuery] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [unenrollingCourseId, setUnenrollingCourseId] = useState(null);
  const [showAddCoursePanel, setShowAddCoursePanel] = useState(false);
  
  const [studentProgress, setStudentProgress] = useState([]);
  const [messageData, setMessageData] = useState({
    title: '',
    body: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [studentsRes, coursesRes] = await Promise.all([
        getAllStudents(),
        getAllCourses()
      ]);

      if (studentsRes.success) {
        setStudents(studentsRes.data || []);
      }
      if (coursesRes.success) {
        setCourses(coursesRes.data || []);
      }
    } catch (error) {
      showNotification('Failed to load data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewProgress = async (student) => {
    setSelectedStudent(student);
    setDetailedProgress({});
    setSelectedProgressCourseId(null);

    const result = {};

    if (student.courses?.length > 0) {
      for (const enrollment of student.courses) {
        const cId = enrollment.courseId;
        try {
          const [courseRes, labRes, quizRes, examRes, capRes, gradeRes] = await Promise.all([
            getCourseById(cId),
            getLabProgress(student.loginId, cId),
            getQuizProgress(student.loginId, cId),
            getExamProgress(student.loginId, cId),
            getCapstoneProgress(student.loginId, cId),
            getCourseGrade(student.loginId, cId),
          ]);

          result[cId] = {
            courseInfo:        courseRes.success  ? courseRes.data  : null,
            combinedProgress: {
              labProgress:      labRes.success   ? labRes.data   : { labs: [] },
              quizProgress:     quizRes.success  ? quizRes.data  : { quizzes: [] },
              examProgress:     examRes.success  ? examRes.data  : { exams: [] },
              capstoneProjects: capRes.success   ? capRes.data   : { capstones: [] },
            },
            courseGrade:       gradeRes.success  ? gradeRes.data  : null,
            enrollmentDate:    enrollment.enrolledAt,
            completionStatus:  enrollment.status,
          };
        } catch (err) {
          console.error(`Progress load failed for ${cId}`, err);
        }
      }
    }

    setDetailedProgress(result);
    setSelectedProgressCourseId(student.courses?.[0]?.courseId || null);
    setProgressModalVisible(true);
  };

  const handleDeleteStudent = async () => {
    try {
      const response = await deleteStudent(selectedStudent.loginId, authData.id);
      if (response.success) {
        showNotification('Student deleted successfully', 'success');
        setAlertVisible(false);
        setModalVisible(false);
        loadData();
      } else {
        showNotification(response.message || 'Failed to delete student', 'error');
      }
    } catch (error) {
      showNotification('Failed to delete student', 'error');
    }
  };

  const handleOpenBilling = async (student) => {
    setBillingLoading(true);
    setBillingTab('payments');
    setBillingData({ payments: [], subscriptions: [] });
    setBillingModalVisible(true);

    try {
      const [paymentsRes, subscriptionsRes] = await Promise.all([
        getPaymentHistory(student.loginId),
        getSubscriptions(student.loginId),
      ]);

      setBillingData({
        payments:      paymentsRes.success      ? paymentsRes.data      || [] : [],
        subscriptions: subscriptionsRes.success ? subscriptionsRes.data || [] : [],
      });

      console.log('payment sample:', paymentsRes.data?.[0]);
      console.log('subscription sample:', subscriptionsRes.data?.[0]);

    } catch (err) {
      showNotification('Failed to load billing data', 'error');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      const res = await cancelSubscription(selectedStudent.loginId, subscriptionId);
      if (res.success) {
        showNotification('Subscription cancelled', 'success');
        // Refresh subscriptions
        const subRes = await getSubscriptions(selectedStudent.loginId);
        setBillingData(prev => ({
          ...prev,
          subscriptions: subRes.success ? subRes.data || [] : prev.subscriptions,
        }));
      } else {
        showNotification(res.message || 'Failed to cancel subscription', 'error');
      }
    } catch (err) {
      showNotification('Failed to cancel subscription', 'error');
    }
  };

  const handleRefund = async () => {
    if (!selectedPayment) return;
    try {
      const res = refundType === 'credit'
        ? await refundToCredit(selectedStudent.loginId, selectedPayment.paymentId)
        : await refundPayment(selectedStudent.loginId, selectedPayment.paymentId);

      if (res.success) {
        showNotification(
          refundType === 'credit' ? 'Refunded to credit balance' : 'Refund processed',
          'success'
        );
        setRefundModalVisible(false);
        setSelectedPayment(null);
        // Refresh payments
        const payRes = await getPaymentHistory(selectedStudent.loginId);
        setBillingData(prev => ({
          ...prev,
          payments: payRes.success ? payRes.data || [] : prev.payments,
        }));
      } else {
        showNotification(res.message || 'Refund failed', 'error');
      }
    } catch (err) {
      showNotification('Refund failed', 'error');
    }
  };

  const handleOpenCoursesModal = (student) => {
    setSelectedStudent(student);
    setShowAddCoursePanel(false);
    setEnrollSearchQuery('');
    setCoursesModalVisible(true);
  };

  const handleUnenroll = async (student, courseId) => {
    setUnenrollingCourseId(courseId);
    try {
      const res = await unenrollCourse(student.loginId, courseId);
      if (res.success) {
        showNotification('Student unenrolled successfully', 'success');
        await loadData(); // refresh student list
        // update selectedStudent in place so modal reflects change
        setSelectedStudent(prev => ({
          ...prev,
          courses: prev.courses.filter(c => c.courseId !== courseId),
        }));
      } else {
        showNotification(res.message || 'Failed to unenroll', 'error');
      }
    } catch (err) {
      showNotification('Failed to unenroll', 'error');
    } finally {
      setUnenrollingCourseId(null);
    }
  };

  const handleEnroll = async (courseId) => {
    setEnrolling(true);
    try {
      const res = await enrollCourse(selectedStudent.loginId, courseId);
      if (res.success) {
        showNotification('Student enrolled successfully', 'success');
        await loadData();
        setSelectedStudent(prev => ({
          ...prev,
          courses: [
            ...prev.courses,
            { courseId, status: 'in-progress', enrolledAt: new Date().toISOString() },
          ],
        }));
        setShowAddCoursePanel(false);
        setEnrollSearchQuery('');
      } else {
        showNotification(res.message || 'Failed to enroll', 'error');
      }
    } catch (err) {
      showNotification('Failed to enroll', 'error');
    } finally {
      setEnrolling(false);
    }
  };

  const handleSendMessage = async () => {
    try {
      if (!messageData.title || !messageData.body) {
        showNotification('Please fill in all message fields', 'error');
        return;
      }

      const messagePayload = {
        targetUserId: selectedStudent.loginId,
        targetName: selectedStudent.name,
        title: messageData.title,
        body: messageData.body,
        senderUserId: authData.id,
        senderName: authData.name,
        type: 'general',
        priority: 'medium',
        actionRequired: false
      };

      const response = await sendMessage(messagePayload);

      if (response.success) {
        showNotification('Message sent successfully', 'success');
        setBillingModalVisible(false);
        setMessageData({ title: '', body: '' });
      } else {
        showNotification(response.message || 'Failed to send message', 'error');
      }
    } catch (error) {
      showNotification('Failed to send message', 'error');
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = !searchQuery || 
      student.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.loginId?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = !filterStatus || student.accountStatus === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const calculateProgress = (student) => {
    if (!student.courses || student.courses.length === 0) return 0;
    
    const completed = student.courses.filter(
      c => c.status === 'completed'
    ).length;
    
    return Math.round((completed / student.courses.length) * 100);
  };

  // renderAdminProgressTable`
  const renderAdminProgressTable = (items, type, icon, title, combinedProgress, courseInfo) => {
    if (!items || items.length === 0) return (
      <div className="grading-category">
        <div className="grading-category-header"><h3>{icon} {title} (0)</h3></div>
        <p className="no-submissions">No {type} items available</p>
      </div>
    );

    return (
      <div className="grading-category">
        <div className="grading-category-header">
          <h3>{icon} {title} ({items.length})</h3>
        </div>
        <div className="submissions-table-container">
          <table className="submissions-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="center">Attempts</th>
                <th className="center">Best Grade</th>
                <th className="center">Avg Grade</th>
                <th className="center">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const idField =
                  type === 'lab'      ? 'labId'      :
                  type === 'quiz'     ? 'quizId'     :
                  type === 'exam'     ? 'examId'     : 'capstoneId';
                const itemId = item[idField];

                let progress = null, attempts = [], bestGrade = 0, avgGrade = 0;

                if (type === 'lab') {
                  progress  = combinedProgress?.labProgress?.labs?.find(l => l.labId === itemId);
                  attempts  = progress?.attempts || [];
                  bestGrade = progress?.bestGrade || 0;
                  avgGrade  = progress?.averageGrade || 0;
                } else if (type === 'quiz') {
                  progress  = combinedProgress?.quizProgress?.quizzes?.find(q => q.quizId === itemId);
                  attempts  = progress?.attempts || [];
                  bestGrade = progress?.bestGrade || 0;
                  avgGrade  = progress?.averageGrade || 0;
                } else if (type === 'exam') {
                  progress  = combinedProgress?.examProgress?.exams?.find(e => e.examId === itemId);
                  attempts  = progress?.attempts || [];
                  bestGrade = progress?.finalGrade || 0;
                  avgGrade  = progress?.finalGrade || 0;
                } else if (type === 'capstone') {
                  progress  = combinedProgress?.capstoneProjects?.capstones?.find(c => c.capstoneId === itemId);
                  attempts  = progress?.submissions || [];
                  bestGrade = progress?.bestGrade || 0;
                }

                let status = 'Not Started';
                if (attempts.length > 0) {
                  const last = attempts[attempts.length - 1];
                  status =
                    last.status === 'graded'    ? 'Graded'      :
                    last.status === 'submitted' ? 'Pending'     : 'In Progress';
                }

                return (
                  <tr key={idx}>
                    <td>
                      <div className="item-info">
                        <strong>{itemId}</strong>
                        <div className="item-title">{item.title}</div>
                      </div>
                    </td>
                    <td className="text-center">
                      <span className="attempts-badge">
                        {attempts.length} / {item.attemptsAllowed || 1}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="grade-display">
                        {bestGrade.toFixed(1)} / {item.maxGrade}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="grade-display">
                        {type !== 'exam' ? avgGrade.toFixed(1) : '-'}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`status-badge ${
                        status === 'Graded'      ? 'graded'      :
                        status === 'Pending'     ? 'pending'     :
                        status === 'In Progress' ? 'in-progress' : 'not-started'
                      }`}>
                        {status === 'Graded'      && '✅ Graded'}
                        {status === 'Pending'     && '⏳ Pending'}
                        {status === 'In Progress' && '📝 In Progress'}
                        {status === 'Not Started' && '🔒 Not Started'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // renderAdminOverallStats`
  const renderAdminOverallStats = (combinedProgress, courseInfo, courseGrade) => {
    const rows = [
      { label: '🧪 Labs',      items: combinedProgress?.labProgress?.labs,             courseItems: courseInfo?.labs,      gradeKey: 'labsGrade',      idKey: 'labId',      gradeVal: g => g.bestGrade },
      { label: '📋 Quizzes',   items: combinedProgress?.quizProgress?.quizzes,         courseItems: courseInfo?.quizzes,   gradeKey: 'quizzesGrade',   idKey: 'quizId',     gradeVal: g => g.bestGrade },
      { label: '📄 Exams',     items: combinedProgress?.examProgress?.exams,           courseItems: courseInfo?.exams,     gradeKey: 'examsGrade',     idKey: 'examId',     gradeVal: g => g.finalGrade },
      { label: '📘 Capstones', items: combinedProgress?.capstoneProjects?.capstones,   courseItems: courseInfo?.capstones, gradeKey: 'capstoneGrade',  idKey: 'capstoneId', gradeVal: g => g.bestGrade || g.finalGrade },
    ];

    const isPending = courseGrade?.letterGrade === 'P';
    const passed    = !isPending && courseGrade?.letterGrade !== 'F';

    return (
      <div className="grading-category">
        <div className="grading-category-header">
          <h3>📈 Overall Course Statistics</h3>
        </div>
        <div className="submissions-table-container">
          <table className="submissions-table">
            <thead>
              <tr>
                <th>Category</th>
                <th className="center">Submitted</th>
                <th className="center">Total</th>
                <th className="center">Avg Grade</th>
                <th className="center">Weighted Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ label, items, courseItems, gradeKey, idKey, gradeVal }, ri) => {
                const graded = (items || []).filter(i => (gradeVal(i) || 0) > 0);
                const avgPct = graded.length === 0 ? 0 : graded.reduce((s, i) => {
                  const max = courseItems?.find(c => c[idKey] === i[idKey])?.maxGrade || 1;
                  return s + ((gradeVal(i) / max) * 100);
                }, 0) / graded.length;
                const weight = Math.round((courseItems?.reduce((s, c) => s + (c.gradeWeight || 0), 0) || 0) * 1000) / 10;

                return (
                  <tr key={ri}>
                    <td><strong>{label}</strong></td>
                    <td className="text-center">{(items || []).length}</td>
                    <td className="text-center">{courseItems?.length || 0}</td>
                    <td className="text-center">
                      <span className="grade-display">{avgPct.toFixed(1)}%</span>
                    </td>
                    <td className="text-center">
                      <span className="grade-display">
                        {courseGrade?.[gradeKey]?.toFixed(1) || '0.0'}%{' '}
                        <small>(wt {weight}%)</small>
                      </span>
                    </td>
                  </tr>
                );
              })}

              {/* Overall row */}
              <tr style={{ fontWeight: 'bold', background: 'var(--color-background-info)' }}>
                <td>📊 <strong>Overall</strong></td>
                <td className="text-center" colSpan={3} />
                <td className="text-center">
                  <span className="grade-display" style={{ color: '#2563eb', fontSize: '1.1em' }}>
                    {(courseGrade?.finalGrade || 0).toFixed(1)}%
                    <span style={{
                      marginLeft: '8px', padding: '2px 8px', borderRadius: '10px',
                      fontSize: '11px', fontWeight: 700,
                      background: isPending ? 'var(--m-amber-dim)' : passed ? 'var(--m-green-dim)' : 'var(--m-red-dim)',
                      color:      isPending ? 'var(--m-amber)'     : passed ? 'var(--m-green)'     : 'var(--m-red)',
                    }}>
                      {isPending ? '⏳ Pending' : passed ? '✅ Pass' : '❌ Fail'}
                    </span>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const profileHeaderData = { 
      ...authData, 
      title: '👨‍🎓 Student Management',
      // subTitle: `${courseInfo?.courseId}`
  };

  return (
    <div className="main-container">
      <div className="main-content">

        {/* Header */}
        <div className="header-row">
          <ProfileHeader data={profileHeaderData} activePage="admin-students" />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={0} icons={[]} />
        </div>

        {/* Tab Navigation */}
        <div className="top-tab-container">
          <BackButton 
            to="/admin/dashboard"
            activeTab="management"
            label="←"
          />
        </div>

        <div className="asm-page-body">

          {/* ── Summary Stats Bar ── */}
          <div className="asm-stats-row">
            <div className="asm-stat-card asm-stat-total">
              <div className="asm-stat-icon">👥</div>
              <div className="asm-stat-info">
                <div className="asm-stat-number">{students.length}</div>
                <div className="asm-stat-label">Total Students</div>
              </div>
            </div>
            <div className="asm-stat-card asm-stat-active">
              <div className="asm-stat-icon">✅</div>
              <div className="asm-stat-info">
                <div className="asm-stat-number">
                  {students.filter(s => s.accountStatus === 'active').length}
                </div>
                <div className="asm-stat-label">Active</div>
              </div>
            </div>
            <div className="asm-stat-card asm-stat-suspended">
              <div className="asm-stat-icon">⛔</div>
              <div className="asm-stat-info">
                <div className="asm-stat-number">
                  {students.filter(s => s.accountStatus === 'suspended').length}
                </div>
                <div className="asm-stat-label">Suspended</div>
              </div>
            </div>
            <div className="asm-stat-card asm-stat-enrollments">
              <div className="asm-stat-icon">📚</div>
              <div className="asm-stat-info">
                <div className="asm-stat-number">
                  {students.reduce((sum, s) => sum + (s.courses?.length || 0), 0)}
                </div>
                <div className="asm-stat-label">Enrollments</div>
              </div>
            </div>
          </div>

          {/* ── Search and Filter ── */}
          <div className="asm-search-row">
            <input
              type="text"
              placeholder="🔍 Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="asm-search-input"
            />
            <select
              className="asm-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">📊 All Status</option>
              <option value="active">✅ Active</option>
              <option value="suspended">⛔ Suspended</option>
              <option value="deleted">🗑️ Deleted</option>
            </select>
          </div>

          {/* ── Results Count ── */}
          {!isLoading && (
            <div className="asm-results-count">
              Showing <strong>{filteredStudents.length}</strong> of{' '}
              <strong>{students.length}</strong> students
              {searchQuery && <span> matching <em>"{searchQuery}"</em></span>}
            </div>
          )}

          {/* ── Student Cards ── */}
          {isLoading ? (
            <div className="student-loading-container">
              <div className="student-loading-spinner" />
              <p className="student-loading-text">Loading students...</p>
            </div>
          ) : filteredStudents.length > 0 ? (
            <div className="asm-cards-grid">
              {filteredStudents.map((student) => {
                const progress = calculateProgress(student);
                const completedCount = student.courses?.filter(c => c.status === 'completed').length || 0;
                return (
                  <div
                    key={student.loginId}
                    className="asm-student-card"
                    onClick={() => {
                      setSelectedStudent(student);
                      setModalVisible(true);
                    }}
                  >
                    {/* minus badge */}
                    <button
                      className="asm-unenroll-badge"
                      onClick={(e) => {
                        e.stopPropagation(); // prevent opening the detail modal
                        setSelectedStudent(student);
                        setAlertVisible(true);
                      }}
                    >
                      −
                    </button>
                    <span className={`asm-status-badge asm-status-${student.accountStatus || 'active'}`}>
                      {student.accountStatus || 'active'}
                    </span>

                    <img
                      src={student.imageUrl || '/images/placeholder.png'}
                      alt={student.name}
                      className="asm-avatar"
                    />

                    <h3 className="asm-card-name">{student.name}</h3>
                    <p className="asm-card-email">{student.email}</p>

                    <div className="asm-chips-row">
                      <span className="asm-chip asm-chip-courses">
                        📚 {student.courses?.length || 0} enrolled
                      </span>
                      {completedCount > 0 && (
                        <span className="asm-chip asm-chip-completed">
                          ✅ {completedCount} done
                        </span>
                      )}
                    </div>

                    {progress > 0 && (
                      <div className="asm-progress-section">
                        <div className="asm-progress-label">
                          <span>Progress</span>
                          <span>{progress}%</span>
                        </div>
                        <div className="asm-progress-track">
                          <div className="asm-progress-fill" style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          ) : (
            <div className="student-empty-state">
              <div className="student-empty-icon">👨‍🎓</div>
              <h3 className="student-empty-title">No students found</h3>
              <p className="student-empty-text">
                {searchQuery
                  ? `No results for "${searchQuery}". Try a different search.`
                  : 'No students match the selected filter.'}
              </p>
            </div>
          )}

        </div>
      </div>

      {/* Student Detail Modal */}
      {modalVisible && selectedStudent && (
        <div className="modal-overlay" onClick={() => setModalVisible(false)}>
          <div className="modal-content-top" onClick={e => e.stopPropagation()}>

            <div className="modal-header-top">
              <div className="student-modal-header" style={{ border: 'none', margin: 0, padding: 0 }}>
                <img
                  src={selectedStudent.imageUrl || '/images/default-avatar.png'}
                  alt={selectedStudent.name}
                  className="student-modal-avatar"
                />
                <div>
                  <h3>{selectedStudent.name}</h3>
                  <p>{selectedStudent.loginId}</p>
                </div>
              </div>
              <button className="close-button" 
                onClick={() => {
                  setModalVisible(false);
                  setProgressModalVisible(false);
                  setBillingModalVisible(false);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">

              {/* Stats */}
              <div className="student-stats-cards-grid">
                <div className="stat-card-gradient status">
                  <div className="stat-card-label">📊 Status</div>
                  <div className="stat-card-value">
                    {selectedStudent.accountStatus?.toUpperCase() || 'ACTIVE'}
                  </div>
                </div>
                <div className="stat-card-gradient enrolled">
                  <div className="stat-card-label">📚 Courses</div>
                  <div className="stat-card-value">
                    {selectedStudent.courses?.length || 0}
                  </div>
                </div>
                <div className="stat-card-gradient completed">
                  <div className="stat-card-label">✅ Completed</div>
                  <div className="stat-card-value">
                    {selectedStudent.courses?.filter(c => c.status === 'completed').length || 0}
                  </div>
                </div>
                <div className="stat-card-gradient progress">
                  <div className="stat-card-label">📈 Progress</div>
                  <div className="stat-card-value">
                    {calculateProgress(selectedStudent)}%
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="instructor-actions-group" style={{ marginBottom: '24px' }}>
                <CustomButton
                  buttonTitle="📚 Courses"
                  action={() => handleOpenCoursesModal(selectedStudent)}
                  bgColor="#f59e0b"
                />
                <CustomButton
                  buttonTitle="📊 Progress"
                  action={() => handleViewProgress(selectedStudent)}
                  bgColor="#10b981"
                />
                <CustomButton
                  buttonTitle="💳  Billing"
                  action={() => handleOpenBilling(selectedStudent)}
                  bgColor="#3b82f6"
                />
                {/* <CustomButton
                  buttonTitle="🗑️ Delete"
                  action={() => setAlertVisible(true)}
                  bgColor="#ef4444"
                /> */}
              </div>

              {/* Student Information */}
              <div className="student-section-header">
                <h4>ℹ️ Student Information</h4>
              </div>
              <div className="student-info-grid" style={{ marginBottom: '24px' }}>
                <div className="student-info-item">
                  <strong>📧 Email:</strong> {selectedStudent.email}
                </div>
                {selectedStudent.phoneNo && (
                  <div className="student-info-item">
                    <strong>📱 Phone:</strong> {selectedStudent.phoneNo}
                  </div>
                )}
                {selectedStudent.dateOfBirth && (
                  <div className="student-info-item">
                    <strong>🎂 Date of Birth:</strong> {selectedStudent.dateOfBirth}
                  </div>
                )}
                {selectedStudent.gender && (
                  <div className="student-info-item">
                    <strong>👤 Gender:</strong> {selectedStudent.gender}
                  </div>
                )}
                <div className="student-info-item">
                  <strong>📅 Account Created:</strong>{' '}
                  {new Date(selectedStudent.createdAt).toLocaleDateString()}
                </div>
              </div>

              {/* Enrolled Courses */}
              <div className="student-section-header">
                <h4>📚 Enrolled Courses</h4>
              </div>
              {selectedStudent.courses?.length > 0 ? (
                <div className="enrolled-courses-list">
                  {selectedStudent.courses.map((enrollment, idx) => (
                    <div key={idx} className="enrolled-course-item">
                      <p className="enrolled-course-meta">
                        <strong>{enrollment.title} ({enrollment.courseId})</strong>
                      </p>
                      <p className="enrolled-course-meta">
                        Status:{' '}
                        <span className={`student-status-badge ${enrollment.status === 'completed' ? 'active' : 'suspended'}`}
                          style={{ position: 'static', display: 'inline-block', marginLeft: '6px' }}>
                          {enrollment.status || 'in-progress'}
                        </span>
                      </p>
                      <p className="enrolled-course-meta">
                        Enrolled: {new Date(enrollment.enrolledAt).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="student-empty-state" style={{ padding: '30px', margin: '0' }}>
                  <div className="student-empty-icon" style={{ fontSize: '40px' }}>📭</div>
                  <p className="student-empty-text">No courses enrolled yet</p>
                </div>
              )}

            </div>

          </div>
        </div>
      )}

      {coursesModalVisible && selectedStudent && (
        <div className="modal-overlay" onClick={() => setCoursesModalVisible(false)}>
          <div className="modal-content-top" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="modal-header-top">
              <div>
                <h3>📚 Enrolled Courses — {selectedStudent.name}</h3>
                <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: '2px 0 0' }}>
                  {selectedStudent.courses?.length || 0} course(s) enrolled
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Plus button */}
                <button
                  className="asm-add-course-btn"
                  onClick={() => {
                    setShowAddCoursePanel(prev => !prev);
                    setEnrollSearchQuery('');
                  }}
                  title="Enroll in a new course"
                >
                  {showAddCoursePanel ? '✕' : '+'}
                </button>
                <button className="close-button" onClick={() => setCoursesModalVisible(false)}>✕</button>
              </div>
            </div>

            <div className="modal-body">

              {/* ── Add Course Panel ── */}
              {showAddCoursePanel && (
                <div className="asm-add-course-panel">
                  <h4 style={{ margin: '0 0 10px', fontSize: '14px' }}>➕ Enroll in a Course</h4>

                  <input
                    type="text"
                    placeholder="🔍 Search available courses..."
                    value={enrollSearchQuery}
                    onChange={e => setEnrollSearchQuery(e.target.value)}
                    className="asm-search-input"
                    style={{ marginBottom: '12px' }}
                  />

                  <div className="asm-add-course-list">
                    {(() => {
                      const enrolledIds = selectedStudent.courses?.map(c => c.courseId) || [];
                      const available = courses.filter(c =>
                        !enrolledIds.includes(c.courseId) &&
                        (!enrollSearchQuery ||
                          c.title?.toLowerCase().includes(enrollSearchQuery.toLowerCase()) ||
                          c.courseId?.toLowerCase().includes(enrollSearchQuery.toLowerCase()) ||
                          c.category?.toLowerCase().includes(enrollSearchQuery.toLowerCase())
                        )
                      );

                      if (available.length === 0) return (
                        <p style={{ color: 'var(--color-text-secondary)', fontSize: '13px', padding: '12px 0' }}>
                          {enrollSearchQuery ? `No courses match "${enrollSearchQuery}"` : 'No available courses to enroll in'}
                        </p>
                      );

                      return available.map((course) => (
                        <div key={course.courseId} className="asm-available-course-row">
                          <div className="asm-available-course-info">
                            <strong>{course.title}</strong>
                            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                              {course.courseId}
                              {course.category && <span> · {course.category}</span>}
                              {course.level    && <span> · {course.level}</span>}
                            </div>
                          </div>
                          <button
                            className="asm-enroll-btn"
                            disabled={enrolling}
                            onClick={() => handleEnroll(course.courseId)}
                          >
                            {enrolling ? '...' : '+ Enroll'}
                          </button>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {/* ── Enrolled Course Cards ── */}
              {!selectedStudent.courses?.length ? (
                <div className="student-empty-state" style={{ padding: '40px 0' }}>
                  <div className="student-empty-icon">📭</div>
                  <p className="student-empty-title">No courses enrolled</p>
                  <p className="student-empty-text">Click + to enroll this student in a course</p>
                </div>
              ) : (
                <div className="asm-enrolled-cards-grid">
                  {selectedStudent.courses.map((enrollment) => {
                    const courseInfo = courses.find(c => c.courseId === enrollment.courseId);
                    const isUnenrolling = unenrollingCourseId === enrollment.courseId;

                    return (
                      <div key={enrollment.courseId} className="asm-enrolled-course-card">

                        {/* Unenroll badge */}
                        <button
                          className="asm-unenroll-badge"
                          disabled={isUnenrolling}
                          onClick={() => handleUnenroll(selectedStudent, enrollment.courseId)}
                          title="Unenroll from this course"
                        >
                          {isUnenrolling ? '...' : '−'}
                        </button>

                        {/* Course image */}
                        {courseInfo?.imageUrl && (
                          <img
                            src={courseInfo.imageUrl}
                            alt={courseInfo.title}
                            className="asm-enrolled-course-img"
                          />
                        )}

                        {/* Course info */}
                        <div className="asm-enrolled-course-body">
                          <h4 className="asm-enrolled-course-title">
                            {courseInfo?.title || enrollment.courseId}
                          </h4>
                          <p className="asm-enrolled-course-id">{enrollment.courseId}</p>

                          {courseInfo?.category && (
                            <span className="asm-chip asm-chip-courses" style={{ marginBottom: '6px', display: 'inline-block' }}>
                              {courseInfo.category}
                            </span>
                          )}

                          <div className="asm-enrolled-course-meta">
                            {courseInfo?.level && (
                              <span>📊 {courseInfo.level}</span>
                            )}
                            {courseInfo?.duration && (
                              <span>⏱️ {courseInfo.duration}</span>
                            )}
                            {courseInfo?.credits && (
                              <span>🎓 {courseInfo.credits} credits</span>
                            )}
                          </div>
                        </div>

                        {/* Enrollment footer */}
                        <div className="asm-enrolled-course-footer">
                          <span className={`progress-status-badge ${
                            enrollment.status === 'completed'  ? 'completed'  :
                            enrollment.status === 'active' ? 'active' : 
                            enrollment.status === 'in-progress' ? 'in-progress' : 'not-started'
                          }`}>
                            {enrollment.status || 'in-progress'}
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                            {new Date(enrollment.enrolledAt).toLocaleDateString()}
                          </span>
                        </div>

                      </div>
                    );
                  })}
                </div>
              )}

              <div className="modal-actions" style={{ marginTop: '16px' }}>
                <button className="asm-btn-cancel" onClick={() => setCoursesModalVisible(false)}>
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Progress Modal */}
      {progressModalVisible && selectedStudent && (
        <div className="modal-overlay" onClick={() => setProgressModalVisible(false)}>
          <div className="modal-content-top" onClick={e => e.stopPropagation()}>

            <div className="modal-header-top">
              <div className="student-modal-header" style={{ border: 'none', margin: 0, padding: 0 }}>
                <img
                  src={selectedStudent.imageUrl || '/images/default-avatar.png'}
                  alt={selectedStudent.name}
                  className="student-modal-avatar"
                />
                <div className="student-modal-info">
                  <h3>{selectedStudent.name}</h3>
                  <p>{selectedStudent.loginId}</p>
                </div>
              </div>
              <button className="close-button" onClick={() => setModalVisible(false)}>✕</button>
            </div>

            <div className="modal-body">

              {/* Course picker — only shown when enrolled in more than one course */}
              {selectedStudent?.courses?.length > 1 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  {selectedStudent.courses.map(e => (
                    <button
                      key={e.courseId}
                      onClick={() => setSelectedProgressCourseId(e.courseId)}
                      className={`tab-button ${selectedProgressCourseId === e.courseId ? 'active-tab' : ''}`}
                      style={{ fontSize: '12px', padding: '4px 10px' }}
                    >
                      {e.courseId}
                    </button>
                  ))}
                </div>
              )}

              {/* No courses enrolled */}
              {!selectedProgressCourseId ? (
                <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '32px 0' }}>
                  No courses enrolled
                </p>

              /* Loading */
              ) : !detailedProgress[selectedProgressCourseId] ? (
                <div className="student-loading-container">
                  <div className="student-loading-spinner" />
                  <p>Loading progress...</p>
                </div>

              /* Progress tables */
              ) : (() => {
                const {
                  courseInfo,
                  combinedProgress,
                  courseGrade,
                  enrollmentDate,
                  completionStatus,
                } = detailedProgress[selectedProgressCourseId];

                return (
                  <div className="scrollable-list" style={{ maxHeight: '60vh', overflowY: 'auto' }}>

                    {/* Enrollment meta */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span className={`progress-status-badge ${
                        completionStatus === 'completed'  ? 'completed'  :
                        completionStatus === 'in-progress' ? 'in-progress' : 'not-started'
                      }`}>
                        {completionStatus || 'not-started'}
                      </span>
                      <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                        Enrolled: {new Date(enrollmentDate).toLocaleDateString()}
                      </span>
                    </div>

                    {renderAdminProgressTable(courseInfo?.labs,      'lab',      '🧪', 'Labs',      combinedProgress, courseInfo)}
                    {renderAdminProgressTable(courseInfo?.quizzes,   'quiz',     '📋', 'Quizzes',   combinedProgress, courseInfo)}
                    {renderAdminProgressTable(courseInfo?.exams,     'exam',     '📄', 'Exams',     combinedProgress, courseInfo)}
                    {renderAdminProgressTable(courseInfo?.capstones, 'capstone', '📘', 'Capstones', combinedProgress, courseInfo)}
                    {renderAdminOverallStats(combinedProgress, courseInfo, courseGrade)}

                  </div>
                );
              })()}

              <div className="modal-actions" style={{ marginTop: '16px' }}>
                <button className="asm-btn-cancel" onClick={() => setProgressModalVisible(false)}>
                  Close
                </button>
              </div>

            </div>

          </div>
        </div>
      )}

      {/* Billing Modal */}
      {billingModalVisible && selectedStudent && (
        <div className="modal-overlay" onClick={() => setBillingModalVisible(false)}>
          <div className="modal-content-top" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="modal-header-top">
              <h3>💳 Billing — {selectedStudent.name}</h3>
              <button className="close-button" onClick={() => setBillingModalVisible(false)}>✕</button>
            </div>

            <div className="modal-body">

              {/* Tab switcher */}
              <div className="top-tab-container" style={{ marginBottom: '16px' }}>
                <button
                  className={`tab-button ${billingTab === 'payments' ? 'active-tab' : ''}`}
                  onClick={() => setBillingTab('payments')}
                >
                  🧾 Payment History
                </button>
                <button
                  className={`tab-button ${billingTab === 'subscriptions' ? 'active-tab' : ''}`}
                  onClick={() => setBillingTab('subscriptions')}
                >
                  🔄 Subscriptions
                </button>
              </div>

              {billingLoading ? (
                <div className="student-loading-container">
                  <div className="student-loading-spinner" />
                  <p>Loading billing data...</p>
                </div>

              ) : billingTab === 'payments' ? (

                /* ── Payment History ── */
                billingData.payments.length === 0 ? (
                  <div className="student-empty-state" style={{ padding: '40px 0' }}>
                    <div className="student-empty-icon">🧾</div>
                    <p className="student-empty-title">No payment history</p>
                  </div>
                ) : (
                  <div className="submissions-table-container">
                    <table className="submissions-table">

                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Description</th>
                          <th className="center">Amount</th>
                          <th className="center">Method</th>
                          <th className="center">Status</th>
                          <th className="center">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {billingData.payments.map((payment, idx) => (
                          <tr key={idx}>

                            {/* was: payment.createdAt */}
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {new Date(payment.transactionDate).toLocaleDateString()}
                            </td>

                            {/* was: payment.courseId || payment.description */}
                            <td>
                              <div className="item-info">
                                <strong>
                                  {payment.cartItems?.map(i => i.courseName).join(', ') || '—'}
                                </strong>
                                <div className="item-title">
                                  {payment.cartItems?.map(i => i.courseId).join(', ')}
                                </div>
                              </div>
                            </td>

                            {/* amount + currency */}
                            <td className="text-center">
                              <span className="grade-display">
                                {payment.currency} ${(payment.amount || 0).toFixed(2)}
                              </span>
                            </td>

                            {/* paymentMethod — unchanged, already correct */}
                            <td className="text-center">
                              <span className="attempts-badge">
                                {payment.paymentMethod || '—'}
                              </span>
                            </td>

                            {/* status — unchanged */}
                            <td className="text-center">
                              <span className={`status-badge ${
                                payment.status === 'completed' ? 'graded'      :
                                payment.status === 'refunded'  ? 'not-started' :
                                payment.status === 'pending'   ? 'pending'     : 'in-progress'
                              }`}>
                                {payment.status === 'completed' && '✅ Completed'}
                                {payment.status === 'refunded'  && '↩️ Refunded'}
                                {payment.status === 'pending'   && '⏳ Pending'}
                                {payment.status === 'failed'    && '❌ Failed'}
                                {!['completed','refunded','pending','failed'].includes(payment.status) && payment.status}
                              </span>
                            </td>

                            {/* refund buttons — unchanged */}
                            <td className="text-center">
                              {payment.status === 'completed' && (
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                  <button
                                    className="view-details-btn"
                                    style={{ background: '#ef4444', color: '#fff', fontSize: '11px' }}
                                    onClick={() => { setSelectedPayment(payment); setRefundType('refund'); setRefundModalVisible(true); }}
                                  >
                                    ↩️ Refund
                                  </button>
                                  <button
                                    className="view-details-btn"
                                    style={{ background: '#8b5cf6', color: '#fff', fontSize: '11px' }}
                                    onClick={() => { setSelectedPayment(payment); setRefundType('credit'); setRefundModalVisible(true); }}
                                  >
                                    💜 Credit
                                  </button>
                                </div>
                              )}
                            </td>

                          </tr>
                        ))}
                      </tbody>

                    </table>
                  </div>
                )

              ) : (

                /* ── Subscriptions ── */
                billingData.subscriptions.length === 0 ? (
                  <div className="student-empty-state" style={{ padding: '40px 0' }}>
                    <div className="student-empty-icon">🔄</div>
                    <p className="student-empty-title">No active subscriptions</p>
                  </div>
                ) : (
                  <div className="submissions-table-container">
                    <table className="submissions-table">

                      <thead>
                        <tr>
                          <th>Plan</th>
                          <th>Course</th>
                          <th className="center">Billing Cycle</th>
                          <th className="center">Next Billing</th>
                          <th className="center">Amount</th>
                          <th className="center">Status</th>
                          <th className="center">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {billingData.subscriptions.map((sub, idx) => (
                          <tr key={idx}>

                            {/* was: sub.plan */}
                            <td>
                              <div className="item-info">
                                <strong>{sub.planType || '—'}</strong>
                              </div>
                            </td>

                            {/* was: sub.courseId — now show name + id */}
                            <td>
                              <div className="item-info">
                                <strong>{sub.courseName || '—'}</strong>
                                <div className="item-title">{sub.courseId}</div>
                              </div>
                            </td>

                            {/* billingCycle derived from planType */}
                            <td className="text-center">
                              <span className="attempts-badge">
                                {sub.planType?.includes('monthly') ? 'Monthly' :
                                sub.planType?.includes('yearly')  ? 'Yearly'  : sub.planType || '—'}
                              </span>
                            </td>

                            {/* was: sub.nextBillingDate — field doesn't exist, show startDate instead */}
                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                              {sub.startDate
                                ? new Date(sub.startDate).toLocaleDateString()
                                : '—'}
                            </td>

                            {/* amount + currency */}
                            <td className="text-center">
                              <span className="grade-display">
                                {sub.currency} ${(sub.amount || 0).toFixed(2)}
                              </span>
                            </td>

                            {/* was: checking 'cancelled' — API returns 'canceled' (one l) */}
                            <td className="text-center">
                              <span className={`status-badge ${
                                sub.status === 'active'   ? 'graded'      :
                                sub.status === 'canceled' ? 'not-started' : 'pending'
                              }`}>
                                {sub.status === 'active'   && '✅ Active'}
                                {sub.status === 'canceled' && '⛔ Canceled'}
                                {sub.status === 'paused'   && '⏸️ Paused'}
                                {!['active','canceled','paused'].includes(sub.status) && sub.status}
                              </span>
                            </td>

                            {/* canceledAt info + cancel button */}
                            <td className="text-center">
                              {sub.status === 'active' ? (
                                <button
                                  className="view-details-btn"
                                  style={{ background: '#ef4444', color: '#fff', fontSize: '11px' }}
                                  onClick={() => handleCancelSubscription(sub.subscriptionId)}
                                >
                                  ⛔ Cancel
                                </button>
                              ) : sub.canceledAt ? (
                                <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                                  Canceled {new Date(sub.canceledAt).toLocaleDateString()}
                                </span>
                              ) : null}
                            </td>

                          </tr>
                        ))}
                      </tbody>

                    </table>
                  </div>
                )
              )}

              <div className="modal-actions" style={{ marginTop: '16px' }}>
                <button className="asm-btn-cancel" onClick={() => setBillingModalVisible(false)}>
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Refund Confirmation Modal */}
      {refundModalVisible && selectedPayment && (
        <div className="modal-overlay" onClick={() => setRefundModalVisible(false)}>
          <div
            className="modal-content-top"
            style={{ maxWidth: '420px' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header-top">
              <h3>{refundType === 'credit' ? '💜 Refund to Credit' : '↩️ Process Refund'}</h3>
              <button className="close-button" onClick={() => setRefundModalVisible(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="student-info-grid" style={{ marginBottom: '20px' }}>
                <div className="student-info-item">
                  <strong>Student:</strong> {selectedStudent?.name}
                </div>
                <div className="student-info-item">
                  <strong>Payment ID:</strong> {selectedPayment.paymentId}
                </div>
                <div className="student-info-item">
                  <strong>Amount:</strong> ${(selectedPayment.amount || 0).toFixed(2)}
                </div>
                <div className="student-info-item">
                  <strong>Date:</strong> {new Date(selectedPayment.transactionDate).toLocaleDateString()}
                </div>
                {selectedPayment.courseId && (
                  <div className="student-info-item">
                    <strong>Course:</strong>{' '}
                    {selectedPayment.cartItems?.map(i => i.courseName).join(', ') || '—'}
                  </div>
                )}
              </div>

              <p style={{ color: 'var(--color-text-secondary)', fontSize: '13px', marginBottom: '20px' }}>
                {refundType === 'credit'
                  ? 'This will add the amount to the student\'s credit balance for future use.'
                  : 'This will return the amount to the student\'s original payment method. This action cannot be undone.'}
              </p>

              <div className="modal-actions" style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="asm-btn-cancel"
                  onClick={() => setRefundModalVisible(false)}
                >
                  Cancel
                </button>
                <button
                  className="view-details-btn"
                  style={{
                    background: refundType === 'credit' ? '#8b5cf6' : '#ef4444',
                    color: '#fff',
                    padding: '8px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                  onClick={handleRefund}
                >
                  {refundType === 'credit' ? '💜 Confirm Credit' : '↩️ Confirm Refund'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {alertVisible && (
        <CustomAlert
          visible={alertVisible}
          title="Delete Student"
          icon="🗑️"
          message="Are you sure you want to delete this student? This action cannot be undone."
          onConfirm={handleDeleteStudent}
          onCancel={() => setAlertVisible(false)}
        />
      )}

    </div>
  );
};

export default AdminStudentManagement;